// Orchestration client : réseau, prédiction du tank local, interpolation des autres entités, rendu et HUD.
import { DT, TICK_RATE, viewSize, viewZoomForLevel } from '../../../shared/constants';
import { lerp, lerpAngle } from '../../../shared/math';
import { stepMovement } from '../../../shared/movement';
import {
  BULLET_ALLY,
  ET_BULLET,
  ET_MONSTER,
  ET_SHAPE,
  MF_BOSS,
  ET_TANK,
  TF_BOT,
  TF_DISCONNECTED,
  TF_COMPACT,
  TF_HEAVY,
  TF_PROTECTED,
  TF_SHIELD,
  TF_ALLY,
  type BulletTuple,
  type MonsterTuple,
  type SelfState,
  type ServerMsg,
  type ShapeTuple,
  type TankTuple,
} from '../../../shared/protocol';
import { CLASSES, getClass } from '../../../shared/catalog/classes';
import { getLang, t, type Key } from '../i18n';
import { QUICK_MESSAGES } from '../../../shared/modes';
import { audio } from './audio';
import { KeyboardMouse } from './input';
import { NetClient } from './net';
import { Renderer } from './render/renderer';
import { getSettings, onSettings } from './settings';
import { Store, initialHud, type HudState } from './store';
import { TouchControls } from './touch';
import { className, modName } from '../i18n/catalog';
import { MOD_DEFS } from '../../../shared/catalog/mods';

interface Snap {
  k: number;
  tanks: Map<number, TankTuple>;
  bullets: Map<number, BulletTuple>;
  shapes: Map<number, ShapeTuple>;
  monsters: Map<number, MonsterTuple>;
}

interface PendingInput {
  s: number;
  mx: number;
  my: number;
}

/** Délai d'interpolation en ticks (snapshots à 15 Hz + marge de gigue). */
const INTERP_TICKS = 3.5;

const CLASS_IDS = new Set(CLASSES.map((c) => c.id));

export class GameClient {
  readonly hud = new Store<HudState>(initialHud());
  private renderer!: Renderer;
  private net!: NetClient;
  private kbm!: KeyboardMouse;
  private touch!: TouchControls;
  private snaps: Snap[] = [];
  private names = new Map<number, [string, number]>();
  private me: SelfState | null = null;
  private pred = { x: 0, y: 0, vx: 0, vy: 0 };
  private prevPred = { x: 0, y: 0 };
  private offset = { x: 0, y: 0 };
  private pending: PendingInput[] = [];
  private seq = 0;
  private clockOffset: number | null = null;
  private acc = 0;
  private lastFrame = 0;
  private raf = 0;
  private running = false;
  private lastHudPush = 0;
  private toastId = 1;
  private fpsFrames = 0;
  private fpsAt = 0;
  private lastAimAngle = 0;
  private lastAimDist = 0;
  private pingTimer = 0;
  private bytesAt = 0;
  private bytesMark = 0;
  private lastSelfFired = 0;
  /** Écran tactile détecté dès le chargement : HUD compact avant même la première touche. */
  private readonly coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;

  /** Demi-côté de l'aire jouable (prédiction locale identique au serveur). */
  private half = 3600;

  constructor(
    private readonly container: HTMLElement,
    private readonly playerName: string,
    private readonly modeReq: Record<string, unknown> = { mode: 'ffa' },
    private readonly onLeave: (reason: string) => void = () => {},
  ) {}

  async start(): Promise<void> {
    const renderer = await Renderer.create(this.container);
    // Partie quittée pendant le chargement (Retour du téléphone) : rien ne démarre, sinon un client orphelin
    // continuait de jouer en arrière-plan et réécrivait le jeton de reprise.
    if (this.stopped) {
      renderer.destroy();
      return;
    }
    this.renderer = renderer;
    this.renderer.onShapeBreak = (kind) => audio.play('break', 0.4 + kind * 0.15);
    this.renderer.onTankHit = (self) => {
      audio.play('hit', self ? 1 : 0.5);
      if (self) this.hitCue();
    };
    this.kbm = new KeyboardMouse(
      this.container,
      (i) => this.upgradeStat(i),
      (v) => this.setAutofire(v),
      (i) => this.quick(i),
    );
    this.touch = new TouchControls(this.container);
    const af = getSettings().autofire;
    this.kbm.autofire = af;
    this.touch.autofire = af;
    this.hud.set({ autofire: af });
    this.net = new NetClient(
      () => ({ name: this.playerName, lang: getLang(), aspect: this.aspect(), touch: matchMedia('(pointer: coarse)').matches, ...this.modeReq }),
      (m) => this.onMessage(m),
      (s) => this.hud.set({ net: s }),
    );
    this.net.connect();
    void this.refreshWallet();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('pointerdown', this.unlockAudio);
    window.addEventListener('keydown', this.unlockAudio);
    document.addEventListener('visibilitychange', this.onVisibility);
    // Point d'inspection pour les tests de bout en bout (?debug=1), lecture seule.
    if (new URLSearchParams(location.search).has('debug')) (window as unknown as { __tankshift: unknown }).__tankshift = this.debugView();
    this.running = true;
    this.lastFrame = performance.now();
    this.raf = requestAnimationFrame(this.frame);
    this.pingTimer = window.setInterval(() => this.net.send({ t: 'ping', c: performance.now() }), 2000);
  }

  private stopped = false;

  stop(): void {
    this.stopped = true;
    this.running = false;
    cancelAnimationFrame(this.raf);
    clearInterval(this.pingTimer);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pointerdown', this.unlockAudio);
    window.removeEventListener('keydown', this.unlockAudio);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.net?.close();
    this.offColors();
    this.cueEl?.remove();
    this.kbm?.dispose();
    this.touch?.dispose();
    this.renderer?.destroy();
  }

  private unlockAudio = () => audio.unlock();

  /** Onglet caché : la boucle d'affichage s'arrête, donc plus aucune entrée ne part. On envoie tout de suite une
   *  entrée neutre (arrêt, sans tir, visée gardée) pour que le serveur ne continue pas sur la dernière. */
  private onVisibility = () => {
    if (!document.hidden || !this.me) return;
    this.seq++;
    this.net.send({ t: 'in', s: this.seq, mx: 0, my: 0, a: round3(this.lastAimAngle), d: Math.round(this.lastAimDist), f: 0 });
    this.pending.push({ s: this.seq, mx: 0, my: 0 });
  };

  /** Repère visuel du son « touché » : bord de l'écran rougi un court instant (option d'accessibilité). */
  private hitCue(): void {
    if (!getSettings().visualCues) return;
    const el = (this.cueEl ??= Object.assign(document.createElement('div'), { className: 'hit-cue' }));
    if (!el.isConnected) this.container.appendChild(el);
    el.classList.remove('on');
    void el.offsetWidth;
    el.classList.add('on');
  }
  private cueEl: HTMLDivElement | null = null;
  private offColors = onSettings((s) => {
    if (s.colorMode !== this.colorMode) {
      this.colorMode = s.colorMode;
      this.renderer?.applyColors();
    }
  });
  private colorMode = getSettings().colorMode;

  private debugView() {
    return {
      me: () => this.me,
      predicted: () => ({ ...this.pred }),
      visibleTankNames: () => {
        const s = this.snaps[this.snaps.length - 1];
        return s ? [...s.tanks.keys()].map((id) => this.names.get(id)?.[0] ?? '?') : [];
      },
      ownBullets: () => {
        const s = this.snaps[this.snaps.length - 1];
        return s && this.me ? [...s.bullets.values()].filter((b) => b[5] === this.me!.id).length : 0;
      },
      fps: () => this.hud.get().fps,
      pendingInputs: () => this.pending.length,
    };
  }

  private aspect(): number {
    return window.innerWidth / Math.max(1, window.innerHeight);
  }

  private onResize = () => this.net.send({ t: 'aspect', aspect: this.aspect() });

  upgradeStat(i: number): void {
    this.net.send({ t: 'stat', i });
  }

  evolve(id: string): void {
    this.net.send({ t: 'evo', id });
  }

  respawn(): void {
    this.net.send({ t: 'respawn' });
  }

  equipMod(id: string): void {
    this.net.send({ t: 'mod', id });
  }

  cashout(): void {
    this.net.send({ t: 'cashout' });
  }

  useBoost(token: 'tok_x2' | 'tok_x5'): void {
    this.net.send({ t: 'boost', token });
  }

  restore(): void {
    this.net.send({ t: 'restore' });
  }

  resetBuild(): void {
    this.net.send({ t: 'resetBuild' });
  }

  quick(i: number): void {
    this.net.send({ t: 'quick', i });
  }

  lobby(action: 'lock' | 'unlock' | 'kick' | 'close' | 'host', target?: string): void {
    this.net.send({ t: 'lobby', action, target });
  }

  leave(reason = ''): void {
    this.onLeave(reason);
  }

  /** Soldes de jetons du compte (affichage), relus après chaque usage. */
  async refreshWallet(): Promise<void> {
    try {
      const r = await fetch('/api/me', { credentials: 'same-origin' });
      const me = (await r.json()) as { account: unknown; balances: Record<string, number> };
      this.hud.set({ wallet: me.account ? me.balances : null });
    } catch {
      /* hors ligne : on garde l'affichage précédent */
    }
  }

  closeReveal(): void {
    this.hud.set({ reveal: null });
  }

  setAutofire(v: boolean): void {
    this.kbm.autofire = v;
    this.touch.autofire = v;
    this.hud.set({ autofire: v });
  }

  // ─── Réseau ──────────────────────────────────────────────────────────

  private localTicks(now = performance.now()): number {
    return (now / 1000) * TICK_RATE;
  }

  private onMessage(m: ServerMsg): void {
    switch (m.t) {
      case 'welcome':
        this.snaps = [];
        this.pending = [];
        this.clockOffset = null;
        this.names.clear();
        this.hud.set({ death: null });
        break;
      case 'snap':
        this.onSnap(m);
        break;
      case 'world':
        this.renderer.setMap(m.w.map);
        if (m.w.half && m.w.half !== this.half) {
          this.half = m.w.half;
          this.renderer.setPlayArea(m.w.half);
        }
        this.renderer.setPoints(m.w.mode?.points ?? null);
        this.hud.set({ world: m.w, worldAt: performance.now() });
        break;
      case 'lobby':
        this.hud.set({ lobby: m });
        break;
      case 'quick':
        audio.play('notify');
        this.toast(`${m.from} : ${t(`quick_${QUICK_MESSAGES[m.i] ?? 'gg'}` as Key)}`, 'info');
        break;
      case 'err':
        if (m.code !== 'version') this.onLeave(m.code);
        break;
      case 'lb':
        this.hud.set({ top: m.top, rank: m.rank, total: m.total, humans: m.humans, bots: m.bots });
        break;
      case 'dead':
        this.me = null;
        if (!m.info.cashout) audio.play('death');
        this.hud.set({ death: m.info, deathAt: performance.now(), me: null });
        break;
      case 'ev':
        if (m.kind === 'kill') {
          audio.play('kill');
          this.toast(t('killed', { name: m.text ?? '?', xp: m.value ?? 0 }), 'kill');
        } else if (m.kind === 'level') {
          audio.play('level', 0.6);
        } else if (m.kind === 'evo') {
          audio.play('evolve');
          this.toast(t('evolved', { name: CLASS_IDS.has(m.text ?? '') ? className(getClass(m.text!)) : (m.text ?? '') }), 'evo');
        } else if (m.kind === 'bonus_end') this.toast(t('bonusEnded'), 'info');
        else if (m.kind === 'mod') this.toast(t('modEquipped', { name: m.text && MOD_DEFS[m.text] ? modName(MOD_DEFS[m.text]) : (m.text ?? '') }), 'evo');
        else if (m.kind === 'boost_end') this.toast(t('boostEnded'), 'info');
        else if (m.kind === 'locked') {
          audio.play('hit', 1);
          this.toast(t('locked'), 'kill');
        }
        else if (m.kind === 'rotation_warn') {
          audio.play('alert', 0.6);
          this.toast(t('rotationWarn', { s: m.value ?? 0 }), 'info');
        }
        else if (m.kind === 'rotation') {
          this.snaps = [];
          this.toast(t('rotationDone', { map: t(`map_${m.text ?? 'A'}` as Key) }), 'evo');
        } else if (m.kind === 'boss_spawn') {
          audio.play('alert');
          this.toast(t('bossSpawn', { name: t(`boss_${m.text ?? 'bastion'}` as Key) }), 'kill');
        }
        else if (m.kind === 'boss_down') this.toast(t('bossDown', { names: m.text ?? '' }), 'kill');
        else if (m.kind === 'boss_retreat') this.toast(t('bossRetreat'), 'info');
        break;
      case 'token':
        if (m.ok) {
          audio.play('evolve', 0.6);
          this.toast(t(`tokenOk_${m.kind}` as Key), 'evo');
        } else this.toast(t(`tokenErr_${(m.error ?? 'error').split(':')[0]}` as Key, { s: (m.error ?? '').split(':')[1] ?? '' }), 'info');
        void this.refreshWallet();
        break;
      case 'cashout':
        if (m.ok) {
          this.hud.set({ reveal: { result: m.result, detail: m.detail } });
        } else this.toast(t(`cashoutErr_${m.error}` as Key), 'info');
        break;
      case 'pong':
        this.hud.set({ ping: Math.round(performance.now() - m.c) });
        break;
    }
  }

  private onSnap(m: Extract<ServerMsg, { t: 'snap' }>): void {
    if (m.n) for (const [id, v] of Object.entries(m.n)) this.names.set(Number(id), v);
    if (m.fx) for (let i = 0; i < m.fx.length; i += 3) this.renderer.blast(m.fx[i], m.fx[i + 1], m.fx[i + 2]);
    const snap: Snap = { k: m.k, tanks: new Map(), bullets: new Map(), shapes: new Map(), monsters: new Map() };
    for (const e of m.e) {
      if (e[0] === ET_TANK) snap.tanks.set(e[1], e as TankTuple);
      else if (e[0] === ET_BULLET) snap.bullets.set(e[1], e as BulletTuple);
      else if (e[0] === ET_SHAPE) snap.shapes.set(e[1], e as ShapeTuple);
      else if (e[0] === ET_MONSTER) snap.monsters.set(e[1], e as MonsterTuple);
    }
    this.snaps.push(snap);
    if (this.snaps.length > 12) this.snaps.shift();

    const sample = m.k - this.localTicks();
    if (this.clockOffset === null) this.clockOffset = sample;
    else if (sample > this.clockOffset) this.clockOffset += (sample - this.clockOffset) * 0.25;
    else this.clockOffset += (sample - this.clockOffset) * 0.02;

    const wasAlive = !!this.me;
    this.me = m.me;
    if (m.me) {
      if (this.hud.get().death) this.hud.set({ death: null });
      const before = { x: this.pred.x + this.offset.x, y: this.pred.y + this.offset.y };
      this.pred = { x: m.me.x, y: m.me.y, vx: m.me.vx, vy: m.me.vy };
      this.pending = this.pending.filter((p) => p.s > m.ack);
      for (const p of this.pending) stepMovement(this.pred, p.mx, p.my, m.me.spd, m.me.r, DT, m.me.rsp, this.half);
      const ex = before.x - this.pred.x;
      const ey = before.y - this.pred.y;
      if (wasAlive && ex * ex + ey * ey < 150 * 150) this.offset = { x: ex, y: ey };
      else {
        this.offset = { x: 0, y: 0 };
        this.prevPred = { x: this.pred.x, y: this.pred.y };
      }
    }
  }

  // ─── Boucle ──────────────────────────────────────────────────────────

  private frame = (now: number) => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.frame);
    const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    this.acc += dt;
    let steps = 0;
    while (this.acc >= DT && steps < 5) {
      this.clientTick();
      this.acc -= DT;
      steps++;
    }
    if (steps === 5) this.acc = 0;
    this.render(dt, now);
    this.fpsFrames++;
    if (now - this.fpsAt > 1000) {
      const kb = (this.net.bytesIn - this.bytesMark) / 1024 / ((now - this.bytesAt) / 1000 || 1);
      this.bytesMark = this.net.bytesIn;
      this.bytesAt = now;
      this.hud.set({ fps: this.fpsFrames, kbps: Math.round(kb) });
      this.fpsFrames = 0;
      this.fpsAt = now;
    }
    if (now - this.lastHudPush > 100) {
      this.lastHudPush = now;
      const toasts = this.hud.get().toasts.filter((x) => now - x.at < 3500);
      this.updateIntelHud();
      this.hud.set({ me: this.me, myTeam: this.me ? this.me.team : this.hud.get().myTeam, toasts, touch: this.touch.used || (this.coarse && !this.kbm.hasMouse) });
    }
  };

  /** Pas client à 30 Hz : échantillonne les entrées, les envoie et prédit le déplacement local. */
  private clientTick(): void {
    this.prevPred = { x: this.pred.x, y: this.pred.y };
    const me = this.me;
    let mx = 0;
    let my = 0;
    let fire = false;
    const ts = this.touch.sample();
    if (ts.active && !this.kbm.hasMouse) {
      mx = ts.mx;
      my = ts.my;
      fire = ts.fire;
      this.lastAimAngle = ts.aimAngle;
      this.lastAimDist = 120 + ts.aimAmp * 780;
    } else {
      const [sx, sy] = this.renderer.worldToScreen(this.pred.x + this.offset.x, this.pred.y + this.offset.y);
      const k = this.kbm.sample(sx, sy, this.renderer.scale);
      mx = k.mx;
      my = k.my;
      fire = k.fire;
      this.lastAimAngle = k.aimAngle;
      this.lastAimDist = k.aimDist;
      // Le tactile peut quand même déplacer si utilisé avec une souris (tablettes hybrides).
      if (ts.active && (ts.mx || ts.my)) {
        mx = ts.mx;
        my = ts.my;
      }
    }
    if (!me) return;
    this.seq++;
    this.net.send({ t: 'in', s: this.seq, mx: round3(mx), my: round3(my), a: round3(this.lastAimAngle), d: Math.round(this.lastAimDist), f: fire ? 1 : 0 });
    this.pending.push({ s: this.seq, mx, my });
    if (this.pending.length > 90) this.pending.shift();
    stepMovement(this.pred, mx, my, me.spd, me.r, DT, me.rsp, this.half);
  }

  private render(dt: number, now: number): void {
    const r = this.renderer;
    r.beginFrame();
    const alpha = Math.min(1, this.acc / DT);
    this.offset.x *= Math.exp(-dt * 12);
    this.offset.y *= Math.exp(-dt * 12);
    const selfX = lerp(this.prevPred.x, this.pred.x, alpha) + this.offset.x;
    const selfY = lerp(this.prevPred.y, this.pred.y, alpha) + this.offset.y;

    const level = this.me?.level ?? 1;
    const v = viewSize(this.aspect(), viewZoomForLevel(level));
    r.setCamera(selfX, selfY, v.w, v.h);

    if (this.snaps.length > 0 && this.clockOffset !== null) {
      const rt = this.localTicks(now) + this.clockOffset - INTERP_TICKS;
      let i1 = this.snaps.findIndex((s) => s.k >= rt);
      if (i1 < 0) i1 = this.snaps.length - 1;
      const s1 = this.snaps[i1];
      const s0 = this.snaps[Math.max(0, i1 - 1)];
      const span = s1.k - s0.k;
      const f = span > 0 ? Math.max(0, Math.min(1, (rt - s0.k) / span)) : 1;
      const myId = this.me?.id ?? -1;
      // Formes
      for (const [id, b] of s1.shapes) {
        const a = s0.shapes.get(id) ?? b;
        r.shape({ id, x: lerp(a[2], b[2], f), y: lerp(a[3], b[3], f), angle: lerpAngle(a[4], b[4], f), kind: b[5], hp: b[6] });
      }
      // Monstres
      for (const [id, b] of s1.monsters) {
        const a = s0.monsters.get(id) ?? b;
        r.monster({ id, x: lerp(a[2], b[2], f), y: lerp(a[3], b[3], f), angle: lerpAngle(a[4], b[4], f), arch: b[5], zone: b[6], hp: b[7], boss: (b[8] & MF_BOSS) !== 0, telegraph: b[10], r: b[9] });
      }
      // Renseignements des modifications de détection (uniquement à partir de ce qui est visible ou autorisé).
      const info = this.me?.info ?? [];
      if (info.length && this.me) {
        const enemies: [number, number][] = [];
        // Les coéquipiers ne sont pas des menaces (radar).
        for (const [id, b] of s1.tanks) if (id !== myId && (b[9] & TF_ALLY) === 0) enemies.push([b[2], b[3]]);
        const mobs: [number, number, number, boolean][] = [];
        for (const b of s1.monsters.values()) mobs.push([b[2], b[3], b[6], (b[8] & 4) !== 0]);
        r.drawIntel({
          x: selfX,
          y: selfY,
          aim: this.lastAimAngle,
          info,
          intel: this.me.intel,
          allies: this.me.allies,
          enemies,
          mobs,
          range: weaponRange(this.me.cls),
          rear: this.rearAlert,
        });
      } else r.drawIntel(null);
      r.trails = info.includes('trails');
      r.mineSight = info.includes('mineSight');
      // Projectiles
      for (const [id, b] of s1.bullets) {
        const a = s0.bullets.get(id);
        const x = a ? lerp(a[2], b[2], f) : b[2];
        const y = a ? lerp(a[3], b[3], f) : b[3];
        r.bullet({ id, x, y, r: b[4], own: b[5] === myId, ally: (b[6] & BULLET_ALLY) !== 0, owner: b[5], style: b[6] & ~BULLET_ALLY, tx: b[7], ty: b[8] });
      }
      // Tanks
      const latest = this.snaps[this.snaps.length - 1];
      for (const [id, b] of s1.tanks) {
        if (id === myId) continue;
        const a = s0.tanks.get(id) ?? b;
        this.drawTank(id, lerp(a[2], b[2], f), lerp(a[3], b[3], f), lerpAngle(a[4], b[4], f), lerpAngle(a[5], b[5], f), b, false);
      }
      // Tank local : position prédite, visée locale immédiate, attributs du dernier snapshot.
      const mine = latest.tanks.get(myId);
      if (mine && this.me) {
        if (mine[10] !== 0 && latest.k !== this.lastSelfFired) {
          this.lastSelfFired = latest.k;
          audio.play('shoot', 0.5);
        }
        const aim = getClass(this.me.cls).weapon.spin ? mine[4] : this.lastAimAngle;
        this.drawTank(myId, selfX, selfY, aim, mine[5], mine, true);
      }
    }
    r.endFrame(dt);
  }

  private drawTank(id: number, x: number, y: number, aim: number, hull: number, b: TankTuple, self: boolean): void {
    const flags = b[9];
    const name = this.names.get(id)?.[0] ?? '';
    this.renderer.tank({
      id,
      x,
      y,
      aim,
      hull,
      r: b[6],
      hp: b[7],
      cls: b[8],
      self,
      bot: (flags & TF_BOT) !== 0,
      protected: (flags & TF_PROTECTED) !== 0,
      disconnected: (flags & TF_DISCONNECTED) !== 0,
      fired: b[10],
      level: b[11],
      name,
      autoAim: b[12] ?? 0,
      skin: b[13] ?? 0,
      decor: flags & TF_COMPACT ? 1 : flags & TF_HEAVY ? 2 : 0,
      ally: (flags & TF_ALLY) !== 0,
      shield: (flags & TF_SHIELD) !== 0,
    });
  }

  private rearAlert = false;

  /** Alerte arrière et télémétrie, calculées à partir des seules entités visibles. */
  private updateIntelHud(): void {
    const me = this.me;
    const s = this.snaps[this.snaps.length - 1];
    if (!me || !s) {
      this.rearAlert = false;
      return;
    }
    const info = me.info;
    let alert = false;
    if (info.includes('rearAlert')) {
      for (const b of s.bullets.values()) {
        // Ses propres tirs et ceux des coéquipiers ne blessent pas : pas d'alerte.
        if (b[5] === me.id || (b[6] & BULLET_ALLY) !== 0) continue;
        const dx = b[2] - this.pred.x;
        const dy = b[3] - this.pred.y;
        const d = Math.hypot(dx, dy);
        if (d > 380) continue;
        const rel = Math.abs(((Math.atan2(dy, dx) - this.lastAimAngle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
        if (rel > (2 * Math.PI) / 3) {
          alert = true;
          break;
        }
      }
    }
    this.rearAlert = alert;
    let tele: number | null = null;
    if (info.includes('telemetry')) {
      const cx = this.pred.x + Math.cos(this.lastAimAngle) * this.lastAimDist;
      const cy = this.pred.y + Math.sin(this.lastAimAngle) * this.lastAimDist;
      let best = 90;
      const consider = (x: number, y: number) => {
        const d = Math.hypot(x - cx, y - cy);
        if (d < best) {
          best = d;
          tele = Math.round(Math.hypot(x - this.pred.x, y - this.pred.y));
        }
      };
      for (const [id, b] of s.tanks) if (id !== me.id) consider(b[2], b[3]);
      for (const b of s.monsters.values()) consider(b[2], b[3]);
      for (const b of s.shapes.values()) consider(b[2], b[3]);
    }
    this.hud.set({ rearAlert: alert, telemetry: tele });
  }

  private toast(text: string, kind: 'kill' | 'level' | 'evo' | 'info'): void {
    const toasts = [...this.hud.get().toasts, { id: this.toastId++, text, kind, at: performance.now() }].slice(-4);
    this.hud.set({ toasts });
  }
}

/** Portée approximative du tube principal (cercle de portée lisible). */
function weaponRange(clsId: string): number {
  const b = getClass(clsId).weapon.barrels.find((x) => !x.auto);
  if (!b) return 600;
  if (b.proj?.lob) return b.proj.lob.maxRange;
  if (b.proj?.kind === 'drone') return b.proj.control ?? 700;
  return 520 * (b.speed ?? 1) * 1.6 * (b.life ?? (b.proj?.kind === 'mine' ? 7 : 1));
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}
